import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Task } from "./useTasks";

export const useDueTasks = () => {
  return useQuery({
    queryKey: ['due-tasks'],
    queryFn: async (): Promise<Task[]> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();

      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .not('status', 'in', '(done,cancelled)') // Exclude completed and cancelled tasks
        .not('due_date', 'is', null) // Only tasks with due dates
        .lte('due_date', today) // Due today or overdue
        .order('due_date', { ascending: true });

      if (error) {
        throw new Error(error.message);
      }

      // Filter to include only tasks where user is creator, assignee, or linked
      let filteredData = data || [];

      // Get tasks where user is linked via task_users table
      const { data: linkedTasks, error: linkedError } = await supabase
        .from('task_users')
        .select('task_id')
        .eq('user_id', user.id);
      
      if (linkedError)
        console.warn('Error fetching linked tasks:', linkedError);
      
      const linkedTaskIds = new Set<string>((linkedTasks || []).map(t => t.task_id));
      
      // Filter: include tasks where created OR assigned OR linked
      filteredData = filteredData.filter(task => {
        const isCreator = task.user_id === user.id;
        const isAssigned = task.assigned_to === user.id;
        const isLinked = linkedTaskIds.has(task.id);
        return isCreator || isAssigned || isLinked;
      });

      // Limit to 10 most urgent tasks after filtering
      filteredData = filteredData.slice(0, 10);

      // If we have tasks with assignees, fetch the assignee information separately
      if (filteredData && filteredData.length > 0) {
        const assigneeIds = filteredData
          .filter(task => task.assigned_to)
          .map(task => task.assigned_to)
          .filter((id, index, arr) => arr.indexOf(id) === index); // Remove duplicates

        if (assigneeIds.length > 0) {
          try {
            const { data: assigneeData, error: assigneeError } = await supabase
              .rpc('get_multiple_users_display_info', {
                p_user_ids: assigneeIds
              });

            if (!assigneeError && assigneeData) {
              // Create a map of user_id to user info
              const assigneeMap = new Map(
                assigneeData.map(user => [user.user_id, user])
              );

              // Attach assignee info to tasks
              return filteredData.map(task => ({
                ...task,
                assignee: task.assigned_to ? assigneeMap.get(task.assigned_to) : undefined
              }));
            }
          } catch (assigneeError) {
            console.warn('Failed to fetch assignee information:', assigneeError);
          }
        }
      }

      return filteredData || [];
    },
  });
};
